import { captureException } from "extensions/sentry";
import jsonpack from "jsonpack";
import { toast } from "extensions/toast";

import { store } from "common/store";

import { request } from "common/components/request";

import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { pazienHistory } from "common/components/pazienHistory";

export function updateURL(options, { trigger = false, replace = false } = {}) {
    const q = new Qualifiers({
        value: ""
    });

    if (options.filter && options.filter !== "{}") {
        const f = JSON.stringify(JSON.parse(options.filter)); // remove whitespace from pretified json
        q.addOrReplace(`filter:${Qualifiers.normalizeValue(f)}`);
    }

    if (options.filterBar && options.filterBar !== "") {
        q.addOrReplace(`filterBar:${Qualifiers.normalizeValue(options.filterBar)}`);
    }

    if (options.itemId) {
        q.addOrReplace(`itemId:${options.itemId}`);
    }

    if (options.walk.length) {
        q.addOrReplace(`walk:${jsonpack.pack(options.walk)}`);
    }
    pazienHistory.navigateWithParams(`administrator/reports/${options.id}/${q.toString(true)}`, { trigger, replace });
}

export function UPDATE_ITEM_ID(itemId) {
    return (dispatch) => {
        dispatch({
            type: "UPDATE_ITEM_ID",
            itemId
        });
    };
}

export function REPORTS_UPDATE_STATE_FROM_URL({ qualifiers, id }) {
    return (dispatch, getState) => {
        let parsedParams = {};
        
        try {
            parsedParams = new Qualifiers({ value: qualifiers }).value.reduce((memo, { category, values }) => {
                if (category === "filter") {
                    let parsed;
                    try {
                        parsed = values[0].value.replace(/&quot;/g, "\"");
                    } catch {
                        parsed = void 0;
                    }
                    memo.filter = parsed;
                } else if (category === "walk") {
                    memo.walk = values[0] ? jsonpack.unpack(values[0].value) : void 0;
                } else {
                    memo[category] = values[0] ? values[0].value : void 0;
                }
                return memo;
            }, Object.create(null));
        } catch (error) {
            captureException(error);
            toast.error("There was an error parsing URL params");
        }
        
        dispatch({
            type: "REPORTS_UPDATE_STATE_FROM_URL",
            id,
            parsedParams
        });

        // If there is no active report then set last report from a localStorage
        const state = getState();
        if (state.reports.options.id === "none") {
            dispatch(SET_LAST_ACTIVE_REPORT());
        } else {
            dispatch(REMEMBER_LAST_REPORT());
        }
    };
}

export function GET_ALL_PAZIEN_USERS() {
    return (dispatch, getState) => {
        dispatch({
            type: "GET_ALL_PAZIEN_USERS_START"
        });

        return request("/merchant/55e86c3c1d41c86cf0d1a7d3/users/").then((data) => {
            dispatch({
                type: "GET_ALL_PAZIEN_USERS_SUCCESS",
                data: data.filter(({ superuser, active }) => active && superuser).map((u) => ({
                    id: u._id,
                    first_name: u.first_name,
                    username: u.username,
                    last_name: u.last_name,
                    handle: u.username.split("@")[0],
                    initials: u.first_name[0].toUpperCase() + u.last_name[0].toUpperCase(),
                    value: `${u.first_name} ${u.last_name}`
                }))
            });
        }, (error) => {
            toast.error("There was an error fetching users. Check browser console");
            dispatch({
                type: "GET_ALL_PAZIEN_USERS_ERROR",
                error
            });

            return Promise.reject(error);
        });
    };
}

export function AVAILABLE_REPORTS_FETCH_DATA() {
    return (dispatch, getState) => {
        dispatch({
            type: "AVAILABLE_REPORTS_FETCH_START"
        });

        return request("/admin/reports").then(([data]) => {
            dispatch({
                type: "AVAILABLE_REPORTS_FETCH_SUCCESS",
                data: data.map((r) => ({
                    ...r
                    // parentReportId: r._id === "5bff12c79c172b069e7faeb3" ? "5d83c0026fae83350901499a" : void 0,
                    // childReportId: r._id === "5d83c0026fae83350901499a" ? "5bff12c79c172b069e7faeb3" : void 0
                }))
            });
        }, (error) => {
            toast.error(error.response?.parsed?.description || "There was an error fetching a list of available reports");

            dispatch({
                type: "AVAILABLE_REPORTS_FETCH_ERROR",
                error
            });

            return Promise.reject(error);
        });
    };
}

export function SET_ACTIVE_REPORT(id) {
    return (dispatch, getState) => {
        dispatch({
            type: "SET_ACTIVE_REPORT",
            id
        });
        updateURL(getState().reports.options, { trigger: true });

        // Store report as default in the localStorage
        dispatch(REMEMBER_LAST_REPORT());
    };
}

export function SET_LAST_ACTIVE_REPORT() {
    return (dispatch, getState) => {
        const lastReport = localStorage.getItem("default_report");
        if (lastReport && lastReport.includes("__")) {
            dispatch(SET_ACTIVE_REPORT(lastReport));
        }
    };
}

export function REMEMBER_LAST_REPORT() {
    return (dispatch, getState) => {
        // Store report as default in the localStorage
        localStorage.setItem("default_report", getState().reports.options.id);
    };
}

export function PREFETCH_ACTION_DIALOG({
    id,
    page,
    parentReportId,
    action,
    parentReportItemIds = [],
    queryParams = { filters: "{}" }
} = {}) {
    return request(`/admin/reports/actions/${id}/${parentReportId}/${action}/preload?limit=2000&page=${page}&queryParams=${queryParams}&selectedChildItemIdList=${parentReportItemIds}`, { data: queryParams });
}

export function REPORT_FETCH({
    id,
    page = 1,
    queryParams = { filters: "{}" }
} = {}) {
    return request(`/admin/reports/${id}?page=${page}`, {
        data: queryParams
    });
}

export function SUBMIT_ACTION({
    childReportId,
    parentReportId,
    parentReportItemId,
    selectedChildItemIdList,
    action,
    filter,
    formData = null
} = {}) {
    return (dispatch, getState) => request(`/admin/reports/actions/${childReportId}/${parentReportId}/${action}`, {
        method: "POST",
        data: {
            childReportId,
            parentReportItemId,
            selectedChildItemIdList,
            filter,
            formData
        }
    }).then(() => {
        dispatch({
            type: "REPORTS_ASSIGN_ITEMS_SUCCESS"
        });
    }, (error) => {
        console.error(error);
        toast.error(error.response?.parsed?.description || "There was an error assigning items to parent");

        dispatch({
            type: "REPORTS_ASSIGN_ITEMS_ERROR"
        });
        return Promise.reject(error);
    });
}

export function REPORT_NEW_ITEM_WITH_CHILDREN({ selectedRows, parentReportId }) {
    return (dispatch, getState) => {
        dispatch({
            type: "REPORT_NEW_ITEM_WITH_CHILDREN",
            selectedRows,
            parentReportId
        });

        updateURL(store.getState().reports.options);
    };
}

export function FETCH_REPORT_FORM({ id, itemId }) {
    return request(`/admin/reports/${id}/${itemId}`).catch((error) => {
        toast.error(error.response?.parsed?.description || "There was an error fetching report form");
    });
}

export function EXPORT_REPORT({
    filename, canonical_name, headers, filters = {}, filterBar = {}, backref = null, grid_display_overrides = []
}) {
    return (dispatch, getState) => request("/admin/reports/export", {
        method: "POST",
        data: {
            filename, canonical_name, headers, filters, filterBar, backref, grid_display_overrides
        }
    }).catch((error) => {
        toast.error(error.response?.parsed?.description || "There was an error requesting export of your report item");
    });
}

export function SUBMIT_REPORT_FORM(formData, { id, itemId }) {
    const isNew = itemId === "new";
    const additionalPath = isNew ? "" : `/${itemId}`;
    const url = `/admin/reports/${id}${additionalPath}`;

    return request(url, {
        method: "POST",
        data: formData
    });
}
